import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  MenuItem, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, CircularProgress, Divider, Chip, Tooltip
} from '@mui/material';
import { Add, Delete, ArrowBack, HomeWork } from '@mui/icons-material';
import axios from 'axios';
import AttachmentsPanel from './AttachmentsPanel';
import { useSuppliers } from '../hooks/useSuppliers';
import { fmt, sym } from '../utils/currency';
import { useItems } from '../hooks/useItems';
import { isRealDate } from '../utils/dateValidation';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';
import { useVATGroups } from '../hooks/useVATGroups';

const API = process.env.REACT_APP_API_URL;
const LINE_TYPES = ['Item', 'Service', 'GL Account', 'Fixed Asset'];
const DRAFT_KEY = 'po-draft';

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  lineType: 'Service',
  itemNo: '',
  faNo: '',
  glAccount: '',
  description: '',
  qty: 1,
  unitPrice: '',
  amount: 0,
  vatGroupCode: ''
});

const PurchaseOrderForm = () => {
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const isEdit = Boolean(orderNo);
  const [searchParams] = useSearchParams();

  const { suppliers }           = useSuppliers();
  const { items: allItems }     = useItems();
  const { accounts: glAccounts } = useGLAccounts();
  const { locations }           = useLocations();
  const { vatGroups }           = useVATGroups();

  const [fixedAssets, setFixedAssets]     = useState([]);
  const [postingGroups, setPostingGroups] = useState([]);

  const [supplierCode, setSupplierCode] = useState('');
  const [orderDate,    setOrderDate]    = useState(new Date().toISOString().split('T')[0]);
  const [expectedDate, setExpectedDate] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [lines,        setLines]        = useState([emptyLine()]);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(isEdit);
  const [saving,       setSaving]       = useState(false);
  const [status,       setStatus]       = useState('Open');
  const [draftRestored, setDraftRestored] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Fetch FAs and posting groups on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/fixed-assets`,              { headers }),
      axios.get(`${API}/api/fixed-assets/posting-groups`, { headers }),
    ]).then(([faRes, pgRes]) => {
      setFixedAssets(faRes.data);
      setPostingGroups(pgRes.data);
    }).catch(() => {}); // non-fatal
  }, []); // eslint-disable-line

  // Restore an unsaved draft (new orders only). BC keeps the document record the
  // moment you enter it; we keep a localStorage draft so leaving without posting
  // doesn't lose your work.
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.supplierCode) setSupplierCode(d.supplierCode);
      if (d.orderDate)    setOrderDate(d.orderDate);
      if (d.expectedDate) setExpectedDate(d.expectedDate);
      if (d.locationCode) setLocationCode(d.locationCode);
      if (Array.isArray(d.lines) && d.lines.length)
        setLines(d.lines.map(l => ({ ...l, _key: Date.now() + Math.random() })));
      setDraftRestored(true);
    } catch { localStorage.removeItem(DRAFT_KEY); }
  }, []); // eslint-disable-line

  // Persist the draft 500ms after the last change (new orders only).
  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(() => {
      const hasData = supplierCode ||
        lines.some(l => l.itemNo || l.faNo || l.glAccount || l.description || l.unitPrice);
      if (!hasData) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        supplierCode, orderDate, expectedDate, locationCode,
        lines: lines.map(({ _key, ...rest }) => rest)
      }));
    }, 500);
    return () => clearTimeout(t);
  }, [supplierCode, orderDate, expectedDate, locationCode, lines, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/api/payables/orders/${orderNo}`, { headers })
      .then(({ data }) => {
        setSupplierCode(data.supplierCode);
        setOrderDate(data.orderDate?.split('T')[0] || '');
        setExpectedDate(data.expectedDate?.split('T')[0] || '');
        setLocationCode(data.locationCode || '');
        setLines(data.lines.map(l => ({ ...l, _key: Math.random(), faNo: l.faNo || '' })));
        setStatus(data.status || 'Open');
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [orderNo]); // eslint-disable-line

  // supplier-preselect: open prefilled from the vendor card (new orders only)
  useEffect(() => { const sc = searchParams.get('supplier'); if (sc && !isEdit) setSupplierCode(sc); }, [searchParams, isEdit]);

  const itemsOfType = (type) => allItems.filter(i => i.itemType === type);
  const hasItemLines = lines.some(l => l.lineType === 'Item');

  // Look up acquisitionCostAccount for an FA
  const getAcqAccount = (faNo) => {
    const fa = fixedAssets.find(a => a.faNo === faNo);
    if (!fa) return '';
    const pg = postingGroups.find(p => p.groupCode === fa.faPostingGroup);
    return pg?.acquisitionCostAccount || '';
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };

      if (field === 'lineType') {
        updated.itemNo      = '';
        updated.faNo        = '';
        updated.glAccount   = '';
        updated.description = '';
        updated.unitPrice   = '';
        // FA lines are always qty 1
        if (value === 'Fixed Asset') updated.qty = 1;
      }

      if (field === 'itemNo' && value) {
        const item = allItems.find(i => i.itemNo === value);
        if (item) {
          updated.glAccount   = item.glAccount;
          updated.description = item.itemName;
          updated.unitPrice   = item.unitPrice;
        }
      }

      if (field === 'faNo' && value) {
        const fa = fixedAssets.find(a => a.faNo === value);
        if (fa) {
          updated.glAccount   = getAcqAccount(value);
          updated.description = fa.faName;
          updated.qty         = 1;
        }
      }

      const qty   = parseFloat(field === 'qty'       ? value : updated.qty)       || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      updated.amount = parseFloat((qty * price).toFixed(2));

      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  // VAT is derived client-side for display only; the server recomputes it on save.
  const vatPct  = (code) => vatGroups.find(g => g.code === code)?.vatPercent || 0;
  const lineVAT = (l) => parseFloat(((l.amount || 0) * vatPct(l.vatGroupCode) / 100).toFixed(2));

  const subtotal    = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const totalVAT    = lines.reduce((s, l) => s + lineVAT(l), 0);
  const totalAmount = subtotal + totalVAT;

  const handleSubmit = async () => {
    if (!supplierCode) { setError('Supplier is required'); return; }
    if (hasItemLines && !locationCode) {
      setError('Receive-to Location is required when the order has Item lines'); return;
    }
    for (const l of lines) {
      if (l.lineType === 'Fixed Asset') {
        if (!l.faNo)        { setError(`Line ${l._key}: select a Fixed Asset`); return; }
        if (!l.glAccount)   { setError(`Line with FA ${l.faNo}: posting group has no Acquisition Cost Account configured`); return; }
      } else {
        if (!l.glAccount)          { setError('Each line must have a GL Account'); return; }
      }
      if (!l.description.trim()) { setError('Each line must have a description'); return; }
      if (!l.qty || l.qty <= 0)  { setError('Qty must be greater than zero'); return; }
      if (!l.unitPrice || parseFloat(l.unitPrice) <= 0) { setError('Unit price must be greater than zero'); return; }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        supplierCode, orderDate, expectedDate, locationCode,
        lines: lines.map((l, i) => ({
          lineNo:      i + 1,
          lineType:    l.lineType,
          itemNo:      l.itemNo  || '',
          faNo:        l.faNo    || '',
          glAccount:   l.glAccount,
          description: l.description,
          qty:         parseFloat(l.qty),
          unitPrice:   parseFloat(l.unitPrice),
          amount:      l.amount,
          vatGroupCode: l.vatGroupCode || ''
        }))
      };

      if (isEdit) {
        await axios.put(`${API}/api/payables/orders/${orderNo}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/payables/orders`, payload, { headers });
        localStorage.removeItem(DRAFT_KEY);
      }
      navigate('/payables/orders');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = isEdit && status !== 'Open';

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3} gap={1} flexWrap="wrap">
            <IconButton onClick={() => navigate('/payables/orders')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" color="primary">
              {isEdit ? `Edit Order: ${orderNo}` : 'New Purchase Order'}
            </Typography>
            <Box ml="auto" display="flex" gap={1}>
              {!isReadOnly && (
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving…' : isEdit ? 'Update Order' : 'Create Order'}
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate('/payables/orders')}>
                {isReadOnly ? 'Back' : 'Cancel'}
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {draftRestored && (
            <Alert severity="info" sx={{ mb: 2 }}
              onClose={() => { localStorage.removeItem(DRAFT_KEY); window.location.reload(); }}>
              Restored your unsaved draft. Close this alert to discard it and start fresh.
            </Alert>
          )}
          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This order is <strong>{status}</strong> and cannot be edited.
            </Alert>
          )}
          {isEdit && (
            <Box mb={3}>
              <AttachmentsPanel refType="PurchaseOrder" refId={orderNo} />
            </Box>
          )}

          {/* Header fields */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <TextField select label="Supplier" value={supplierCode}
              onChange={e => setSupplierCode(e.target.value)}
              disabled={isReadOnly}
              sx={{ minWidth: 280 }} size="small">
              <MenuItem value=""><em>Select Supplier</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.supplierCode} value={s.supplierCode}>
                  {s.supplierCode} – {s.supplierName}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Order Date" type="date" value={orderDate}
              onChange={e => setOrderDate(e.target.value)}
              disabled={isReadOnly}
              size="small" InputLabelProps={{ shrink: true }}
              error={!!(orderDate && !isRealDate(orderDate))}
              helperText={orderDate && !isRealDate(orderDate) ? 'Not a valid date' : ''} />
            <TextField label="Expected Date" type="date" value={expectedDate}
              onChange={e => setExpectedDate(e.target.value)}
              disabled={isReadOnly}
              size="small" InputLabelProps={{ shrink: true }}
              inputProps={{ min: orderDate || undefined }}
              error={!!((expectedDate && !isRealDate(expectedDate)) || (orderDate && expectedDate && expectedDate < orderDate))}
              helperText={(expectedDate && !isRealDate(expectedDate)) ? 'Not a valid date' : (orderDate && expectedDate && expectedDate < orderDate) ? 'Before order date' : ''} />
            <TextField select label="Receive-to Location" value={locationCode}
              onChange={e => setLocationCode(e.target.value)}
              disabled={isReadOnly}
              sx={{ minWidth: 220 }} size="small"
              helperText={hasItemLines ? 'Required — items received here' : 'Optional for service/FA orders'}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                  {loc.locationCode} – {loc.locationName}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Order Lines</Typography>

          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}><strong>Type</strong></TableCell>
                <TableCell sx={{ minWidth: 190 }}><strong>Item / FA / GL</strong></TableCell>
                <TableCell sx={{ minWidth: 170 }}><strong>Description</strong></TableCell>
                <TableCell align="center" sx={{ width: 70 }}><strong>Qty</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}><strong>Unit Price</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>VAT</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}><strong>Amount</strong></TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l._key}>

                  {/* Line Type */}
                  <TableCell>
                    <TextField select value={l.lineType} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'lineType', e.target.value)}>
                      {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </TableCell>

                  {/* Item / FA / GL selector */}
                  <TableCell>
                    {l.lineType === 'Fixed Asset' ? (
                      <TextField select value={l.faNo} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'faNo', e.target.value)}>
                        <MenuItem value=""><em>Select Fixed Asset</em></MenuItem>
                        {fixedAssets.map(fa => (
                          <MenuItem key={fa.faNo} value={fa.faNo}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HomeWork fontSize="small" sx={{ color: 'text.secondary' }} />
                              {fa.faNo} — {fa.faName}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : l.lineType === 'GL Account' ? (
                      <TextField select value={l.glAccount} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'glAccount', e.target.value)}>
                        {glAccountOptions(glAccounts)}
                      </TextField>
                    ) : (
                      <TextField select value={l.itemNo} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select {l.lineType}</em></MenuItem>
                        {itemsOfType(l.lineType).map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>
                            {i.itemNo} – {i.itemName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {/* Show acquisition account chip for FA lines */}
                    {l.lineType === 'Fixed Asset' && l.glAccount && (
                      <Tooltip title="Acquisition Cost Account from FA Posting Group">
                        <Chip label={l.glAccount} size="small" color="primary" variant="outlined"
                          sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Description */}
                  <TableCell>
                    <TextField value={l.description} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'description', e.target.value)} />
                  </TableCell>

                  {/* Qty — locked to 1 for FA lines */}
                  <TableCell align="center">
                    <TextField value={l.qty} size="small" type="number"
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                      onChange={e => updateLine(l._key, 'qty', e.target.value)}
                      disabled={isReadOnly || l.lineType === 'Fixed Asset'} />
                  </TableCell>

                  {/* Unit Price = acquisition cost for FA */}
                  <TableCell align="right">
                    <TextField value={l.unitPrice} size="small" type="number"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'unitPrice', e.target.value)}
                      label={l.lineType === 'Fixed Asset' ? 'Acq. Cost' : undefined} />
                  </TableCell>

                  {/* VAT Posting Group */}
                  <TableCell>
                    <TextField select value={l.vatGroupCode || ''} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'vatGroupCode', e.target.value)}>
                      <MenuItem value=""><em>No VAT</em></MenuItem>
                      {vatGroups.map(g => (
                        <MenuItem key={g.code} value={g.code}>
                          {g.code} ({g.vatPercent}%)
                        </MenuItem>
                      ))}
                    </TextField>
                    {l.vatGroupCode && lineVAT(l) > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        VAT {sym()} {fmt(lineVAT(l))}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {sym()} {fmt(l.amount)}
                  </TableCell>

                  <TableCell>
                    {!isReadOnly && (
                      <IconButton size="small" color="error" onClick={() => removeLine(l._key)}
                        disabled={lines.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
            {!isReadOnly && (
              <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">
                Add Line
              </Button>
            )}
            <Box ml="auto" textAlign="right">
              <Typography variant="body2" color="text.secondary">
                Subtotal: {sym()} {fmt(subtotal)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                VAT: {sym()} {fmt(totalVAT)}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                Total: {sym()} {fmt(totalAmount)}
              </Typography>
            </Box>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseOrderForm;
